import { type DragEvent,useCallback, useEffect, useState } from "react";

function dragEventHasFiles(event: DragEvent): boolean {
  if (event.dataTransfer?.types?.includes("Files")) {
    return true;
  }
  return (event.dataTransfer?.items?.length ?? 0) > 0;
}

function isLeavingDropZone(event: DragEvent<HTMLElement>): boolean {
  const related = event.relatedTarget;
  if (!related || !(related instanceof Node)) {
    return true;
  }
  return !event.currentTarget.contains(related);
}

export function useChatFileDragDrop({
  disabled,
  onFiles,
}: {
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(false);

  const reset = useCallback(() => {
    setVisible(false);
    setActive(false);
  }, []);

  useEffect(() => {
    const onWindowDragEnd = () => reset();
    window.addEventListener("dragend", onWindowDragEnd);
    window.addEventListener("drop", onWindowDragEnd);
    return () => {
      window.removeEventListener("dragend", onWindowDragEnd);
      window.removeEventListener("drop", onWindowDragEnd);
    };
  }, [reset]);

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !dragEventHasFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setVisible(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (isLeavingDropZone(event)) {
        reset();
      }
    },
    [disabled, reset],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !dragEventHasFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
      setActive(true);
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      reset();

      const list = event.dataTransfer?.files;
      if (!list || list.length === 0) {
        return;
      }
      onFiles(Array.from(list));
    },
    [disabled, onFiles, reset],
  );

  return {
    visible,
    active,
    zoneProps: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
    },
  };
}
